import { cn } from '@workspace/ui/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { CheckCircle, Info, Loader2, XCircle } from 'lucide-react'
import type * as React from 'react'

const statusCardVariants = cva('rounded-2xl border border-border bg-card p-8 text-center', {
  variants: {
    variant: {
      pending: '',
      success: '',
      error: '',
      info: '',
    },
  },
  defaultVariants: {
    variant: 'info',
  },
})

const iconContainerVariants = cva(
  'mx-auto mb-6 flex size-16 items-center justify-center rounded-full',
  {
    variants: {
      variant: {
        pending: 'bg-amber-500/10',
        success: 'bg-emerald-500/10',
        error: 'bg-red-500/10',
        info: 'bg-blue-500/10',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  }
)

const iconVariants = cva('size-8', {
  variants: {
    variant: {
      pending: 'text-amber-500',
      success: 'text-emerald-500',
      error: 'text-red-500',
      info: 'text-blue-500',
    },
  },
  defaultVariants: {
    variant: 'info',
  },
})

const DefaultIcons: Record<NonNullable<StatusCardProps['variant']>, typeof CheckCircle> = {
  pending: Loader2,
  success: CheckCircle,
  error: XCircle,
  info: Info,
}

export interface StatusCardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof statusCardVariants> {
  icon?: React.ReactNode
  title: string
  description?: string
  action?: React.ReactNode
}

function StatusCard({
  variant = 'info',
  icon,
  title,
  description,
  action,
  className,
  ...props
}: StatusCardProps) {
  const DefaultIcon = variant ? DefaultIcons[variant] : Info
  const isPending = variant === 'pending'

  return (
    <div className={cn(statusCardVariants({ variant }), className)} {...props}>
      <div className={iconContainerVariants({ variant })}>
        {icon ?? (
          <DefaultIcon className={cn(iconVariants({ variant }), isPending && 'animate-spin')} />
        )}
      </div>
      <h2 className="mb-3 text-2xl font-semibold text-foreground">{title}</h2>
      {description && <p className="mb-6 text-muted-foreground">{description}</p>}
      {action}
    </div>
  )
}

export { StatusCard, statusCardVariants }
